import { useQuery } from '@tanstack/react-query'
import { repositories } from '@/data'
import type { AgencyInfo } from '@/data/repositories/types'

/**
 * Pure helper — exported for unit testing.
 * Finds the first agency whose slug matches `orgSlug`.
 */
export function pickAgency(
  list: AgencyInfo[],
  orgSlug: string,
): AgencyInfo | undefined {
  return list.find((a) => a.slug === orgSlug)
}

/**
 * Returns the current agency (id, role, isAdmin) for the given org slug.
 * Role comes from the per-agency membership on the backend — NOT user.role.
 */
export function useCurrentAgency(orgSlug: string) {
  const q = useQuery({
    queryKey: ['agencies'],
    queryFn: () => repositories.auth.listAgencies(),
    staleTime: 60_000,
  })
  const match: AgencyInfo | undefined = q.data ? pickAgency(q.data, orgSlug) : undefined
  return {
    agencyId: match?.id ?? null,
    role: match?.role ?? null,
    isAdmin: match?.role === 'admin',
    // isPending stays true while the first fetch is in-flight (including after
    // a network error, where isLoading would wrongly flip to false in v5).
    isLoading: q.isPending,
    isError: q.isError,
  }
}

/**
 * The caller's role in a specific agency, resolved by id rather than slug.
 *
 * Surfaces nested inside a client workspace already hold an `agencyId` and have
 * no reason to know the org slug. Matching on the id is also the more honest
 * key: the slug is a routing detail, the membership is what actually grants
 * permission.
 */
export function useAgencyRole(agencyId: number | null) {
  const q = useQuery({
    queryKey: ['agencies'],
    queryFn: () => repositories.auth.listAgencies(),
    staleTime: 60_000,
  })
  const match = agencyId == null ? undefined : q.data?.find((a) => a.id === agencyId)
  return { role: match?.role ?? null, isLoading: q.isPending }
}
